import axios, { AxiosError } from 'axios';
import { sendError } from '@pec/aion-ui-core/sendError';
import { ThunkAction } from 'redux-thunk';
import {
  FETCH_COMPANY_CONTACT_REQUEST,
  FETCH_COMPANY_CONTACT_FAILURE,
  FETCH_COMPANY_CONTACT_SUCCESS,
  FETCH_COUNTRIES_REQUEST,
  FETCH_COUNTRIES_SUCCESS,
  FETCH_COUNTRIES_FAILURE
} from '../constants/companyContact';
import { CompanyContactPayload, Country, FetchCountriesParams } from '../interfaces/companyContact';
import { OdataResponse } from '@pec/aion-ui-odata/types/odataResponse';
import { RootState } from '../combineReducers';

const fetchCountriesRequest = () =>
  ({
    type: FETCH_COUNTRIES_REQUEST
  } as const);

const fetchCountriesSuccess = () =>
  ({
    type: FETCH_COUNTRIES_SUCCESS
  } as const);

const fetchCountriesFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: FETCH_COUNTRIES_FAILURE,
    error
  } as const;
};

export const fetchCountries = (
  params: FetchCountriesParams
): ThunkAction<void, RootState, null, Actions> => async dispatch => {
  try {
    dispatch(fetchCountriesRequest());

    const billing = params.billing.replace(/'/g, "''");
    const physical = params.physical.replace(/'/g, "''");

    const { data } = await axios.get<OdataResponse<Country[]>>('/api/v2/Location/countries', {
      params: {
        $filter: `(name eq '${billing}') or (name eq '${physical}')`
      }
    });

    const billingCountry = data.value.find(({ name }) => name && name === params.billing);
    const physicalCountry = data.value.find(({ name }) => name && name === params.physical);
    dispatch(fetchCountriesSuccess());

    return {
      billing: billingCountry?.iso3,
      physical: physicalCountry?.iso3
    } as FetchCountriesParams;
  } catch (error) {
    dispatch(fetchCountriesFailure(error));
    throw error;
  }
};

const fetchCompanyContactRequest = () =>
  ({
    type: FETCH_COMPANY_CONTACT_REQUEST
  } as const);

const fetchCompanyContactSuccess = (payload: CompanyContactPayload) => {
  return {
    type: FETCH_COMPANY_CONTACT_SUCCESS,
    payload
  } as const;
};

const fetchCompanyContactFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: FETCH_COMPANY_CONTACT_FAILURE,
    error
  } as const;
};

export const fetchCompanyContact = (
  companyId: string | number
): ThunkAction<void, RootState, null, Actions> => async dispatch => {
  try {
    dispatch(fetchCompanyContactRequest());

    const { data } = await axios.get<CompanyContactPayload>(`/api/v1/billing/Company/${companyId}`);

    if (data.id) {
      const countries = {
        billing: data.billingAddress.country,
        physical: data.physicalAddress.country
      };

      const countriesResponse = ((await dispatch(fetchCountries(countries))) as unknown) as FetchCountriesParams;

      const billingAddress = { ...data.billingAddress, countryIso3: countriesResponse.billing };
      const physicalAddress = { ...data.physicalAddress, countryIso3: countriesResponse.physical };

      dispatch(fetchCompanyContactSuccess({ ...data, billingAddress, physicalAddress }));
    }
  } catch (error) {
    dispatch(fetchCompanyContactFailure(error));
  }
};

export type Actions =
  | ReturnType<typeof fetchCompanyContactRequest>
  | ReturnType<typeof fetchCompanyContactSuccess>
  | ReturnType<typeof fetchCompanyContactFailure>
  | ReturnType<typeof fetchCountriesRequest>
  | ReturnType<typeof fetchCountriesSuccess>
  | ReturnType<typeof fetchCountriesFailure>;
